import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import UserContext from '../../contexts/UserContext';
import authenticate from '../../utils/Authenticate';
import * as XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import { n2 } from '../../utils/fmt';
import { formatMoney } from '../../utils/currency';

const Reports = () => {
  const { user, setUser } = useContext(UserContext);
  const [menuOptions, setMenuOptions] = useState([]);
  const [selectedMenu, setSelectedMenu] = useState('');
  const [reportData, setReportData] = useState([]);
  const navigate = useNavigate();

  const fetchMenuOptions = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URI}/api/menu/getAll`, {
        withCredentials: true,
      });
      if (response.data.success) {
        setMenuOptions(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching menu options", error);
    }
  };

  const fetchReport = async (menuId) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URI}/api/miqaat/getMenuReport?menuId=${menuId}`, {
        withCredentials: true,
      });
      if (response.data.success) {
        setReportData(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching report data", error);
    }
  };

  useEffect(() => {
    async function startup() {
      if (!user) {
        await authenticate(setUser, navigate);
      }
      if (user) {
        fetchMenuOptions();
      }
    }
    startup();
  }, [user]);

  const handleChange = (e) => {
    const selectedId = e.target.value;
    setSelectedMenu(selectedId);
    if (selectedId) fetchReport(selectedId);
  };

  // Despite its name, this previously hardcoded £ regardless of tenant - fixed
  // to actually route through the shared London-vs-everyone-else currency logic.
  const formatInINR = (num) => formatMoney(user, num);

  const exportToExcel = () => {
    const allRows = [];

    reportData.forEach((zoneReport) => {
      zoneReport.items.forEach((item) => {
        const unitPrice =
          item.totalQuantity > 0 ? item.totalPrice / item.totalQuantity : 0;

        allRows.push({
          Zone: zoneReport.zone,
          Item: item.itemName,
          Quantity: n2(item.totalQuantity),
          Unit: item.unit,
          'Unit Price': n2(unitPrice),
          'Total Price': n2(item.totalPrice),
        });
      });
    });

    const worksheet = XLSX.utils.json_to_sheet(allRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Menu Report');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, 'MenuReport.xlsx');
  };

  const reportSection = (
    <div className="mt-5">
      {reportData && reportData.length > 0 ? (
        <>
          <div className="text-right mb-4 max-w-4xl mx-auto">
            <button
              onClick={exportToExcel}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
            >
              Export to Excel
            </button>
          </div>

          {reportData.map((zoneReport, index) => (
            <div
              key={index}
              className="bg-white rounded-lg shadow-md mt-6 mx-auto max-w-4xl p-4 border border-cyan-900"
            >
              <h3 className="text-lg font-semibold text-lime-800 mb-2">
                Zone: {zoneReport.zone}
              </h3>
              <p className="mb-4 text-gray-700">
                <strong>Total Cost:</strong> {formatInINR(zoneReport.totalCost)}
              </p>

              <table className="w-full border border-collapse">
                <thead className="bg-cyan-100">
                  <tr>
                    <th className="border px-3 py-2 text-left">Item Name</th>
                    <th className="border px-3 py-2 text-center">Quantity</th>
                    <th className="border px-3 py-2 text-center">Unit</th>
                    <th className="border px-3 py-2 text-center">Unit Price</th>
                    <th className="border px-3 py-2 text-right">Total Price</th>
                  </tr>
                </thead>
                <tbody>
                  {zoneReport.items.map((item, itemIdx) => {
                    const unitPrice =
                      item.totalQuantity > 0
                        ? item.totalPrice / item.totalQuantity
                        : 0;
                    return (
                      <tr key={itemIdx} className="bg-slate-50 even:bg-slate-100">
                        <td className="border px-3 py-2">{item.itemName}</td>
                        <td className="border px-3 py-2 text-center">{n2(item.totalQuantity)}</td>
                        <td className="border px-3 py-2 text-center">{item.unit}</td>
                        <td className="border px-3 py-2 text-center">{formatInINR(unitPrice)}</td>
                        <td className="border px-3 py-2 text-right">{formatInINR(item.totalPrice)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </>
      ) : (
        selectedMenu && <p className="text-center mt-4">No report data found.</p>
      )}
    </div>
  );

  return (
    <div className="p-5 bg-gray-50 min-h-screen">
      <h2 className="text-2xl font-semibold mb-4 text-center">Menu Summary Report</h2>
      <div className="mb-6 max-w-md mx-auto">
        <label htmlFor="menu" className="block text-gray-700 mb-2">
          Select Menu:
        </label>
        <select
          id="menu"
          value={selectedMenu}
          onChange={handleChange}
          className="border border-gray-300 rounded-md px-3 py-2 w-full"
        >
          <option value="">-- Select --</option>
          {menuOptions.map((menu) => (
            <option key={menu._id} value={menu._id}>
              {menu.name}
            </option>
          ))}
        </select>
      </div>
      {reportSection}
    </div>
  );
};

export default Reports;
